// src/pages/patient/OrderMedicine.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, MapPin, Star, ShoppingCart, Plus, Minus,
  Pill, FileText, Truck, Store, AlertCircle, CheckCircle, X,
  Upload, Loader2, Package, AlertTriangle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { pharmacyService } from '../../services/pharmacyService';
import apiService from '../../services/api';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Alert from '../../components/ui/Alert';
import Modal from '../../components/common/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Badge from '../../components/ui/Badge';

const OrderMedicine = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = useState(false);
  const [pharmacies, setPharmacies] = useState([]);
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);
  const [medicines, setMedicines] = useState([]);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    maxDistance: 10,
    deliveryService: false,
    sortBy: 'distance'
  });

  // Medicine search
  const [medicineSearch, setMedicineSearch] = useState('');
  const [medicineCategory, setMedicineCategory] = useState('');

  const medicineCategories = [
    'analgesics', 'antibiotics', 'antivirals', 'antifungals',
    'cardiovascular', 'diabetes', 'respiratory', 'gastrointestinal',
    'dermatology', 'vitamins', 'supplements', 'first_aid',
    'women_health', 'mental_health', 'other'
  ];

  // Get user location on mount
  useEffect(() => {
    getCurrentLocation();
  }, []);

  useEffect(() => {
    if (userLocation) {
      loadNearbyPharmacies();
    }
  }, [filters, userLocation]);

  useEffect(() => {
    if (selectedPharmacy) {
      loadPharmacyMedicines();
    }
  }, [selectedPharmacy, medicineSearch, medicineCategory]);

  const getCurrentLocation = async () => {
    setLocationLoading(true);
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          setLocationLoading(false);
        },
        (error) => {
          console.warn('Location access denied:', error);
          setError('Location access denied. Please enable location services.');
          setLocationLoading(false);
        }
      );
    } else {
      setError('Geolocation not supported by your browser');
      setLocationLoading(false);
    }
  };

  const loadNearbyPharmacies = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        latitude: userLocation?.latitude,
        longitude: userLocation?.longitude,
        radius: filters.maxDistance,
        sortBy: filters.sortBy,
        limit: 20,
        isActive: 'true'
      };

      if (filters.deliveryService) {
        params.deliveryService = 'true';
      }

      const response = await pharmacyService.getNearbyPharmacies(params);
      
      if (response.success) {
        setPharmacies(response.data?.pharmacies || []);
      } else {
        setError(response.message || 'Failed to load nearby pharmacies');
        setPharmacies([]);
      }
    } catch (error) {
      console.error('Load pharmacies error:', error);
      setError('Failed to load nearby pharmacies. Please try again.');
      setPharmacies([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPharmacyMedicines = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        search: medicineSearch || undefined,
        category: medicineCategory || undefined,
        isAvailable: true,
        limit: 100
      };

      const response = await pharmacyService.getPharmacyProducts(selectedPharmacy._id, params);
      
      if (response.success) {
        setMedicines(response.data?.products || []);
      } else {
        setError(response.message || 'Failed to load medicines');
        setMedicines([]);
      }
    } catch (error) {
      console.error('Load medicines error:', error);
      setError('Failed to load medicines. Please try again.');
      setMedicines([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOrderClick = (medicine) => {
    if (medicine.stock === 0) {
      setError('This product is currently out of stock');
      return;
    }
    setSelectedMedicine(medicine);
    setShowOrderModal(true);
  };

  const handleOrderSuccess = () => {
    setShowOrderModal(false);
    setSuccess('Order placed successfully! Check your orders page for details.');
    setTimeout(() => {
      navigate('/patient/orders');
    }, 2000);
  };

  const PharmacyCard = ({ pharmacy }) => {
    const isSelected = selectedPharmacy?._id === pharmacy._id;

    return (
      <div
        onClick={() => setSelectedPharmacy(pharmacy)}
        className={`bg-white rounded-lg shadow-sm border-2 cursor-pointer transition-all p-4 ${
          isSelected
            ? 'border-primary-500 shadow-md'
            : 'border-gray-200 hover:border-gray-300'
        }`}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">{pharmacy.name}</h3>
            <p className="text-sm text-gray-600 mt-1">{pharmacy.address}</p>
          </div>
          {isSelected && (
            <CheckCircle className="w-5 h-5 text-primary-600 flex-shrink-0" />
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center">
              <MapPin className="w-3 h-3 text-gray-400 mr-1" />
              <span className="text-xs text-gray-500">
                {pharmacy.distance ? `${pharmacy.distance.toFixed(1)}km` : 'Distance unknown'}
              </span>
            </div>
            {pharmacy.rating?.average && (
              <div className="flex items-center">
                <Star className="w-3 h-3 text-yellow-400 mr-1" />
                <span className="text-xs text-gray-500">{pharmacy.rating.average.toFixed(1)}</span>
              </div>
            )}
          </div>
          {pharmacy.deliveryService && (
            <Badge className="bg-green-100 text-green-800">
              <Truck className="w-3 h-3 mr-1" />
              Delivery
            </Badge>
          )}
        </div>
      </div>
    );
  };

  const MedicineCard = ({ medicine }) => {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-4">
        {/* Product Image */}
        <div className="mb-3">
          {medicine.image ? (
            <img
              src={medicine.image}
              alt={medicine.name}
              className="w-full h-32 object-cover rounded-lg"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center"
            style={{ display: medicine.image ? 'none' : 'flex' }}
          >
            <Package className="w-12 h-12 text-gray-400" />
          </div>
        </div>

        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h4 className="font-medium text-gray-900 line-clamp-1">{medicine.name}</h4>
            {medicine.genericName && (
              <p className="text-sm text-gray-600 mt-1 line-clamp-1">{medicine.genericName}</p>
            )}
            <div className="flex items-center space-x-2 mt-2">
              {medicine.form && (
                <Badge className="bg-blue-100 text-blue-800 text-xs">
                  {medicine.form}
                </Badge>
              )}
              {medicine.dosage && (
                <Badge className="bg-purple-100 text-purple-800 text-xs">
                  {medicine.dosage}
                </Badge>
              )}
            </div>
          </div>
          {medicine.prescriptionRequired && (
            <Badge className="bg-red-100 text-red-800 flex-shrink-0">
              <FileText className="w-3 h-3 mr-1" />
              Rx
            </Badge>
          )}
        </div>

        <div className="flex items-center justify-between pt-3 border-t">
          <div>
            <p className="text-lg font-semibold text-gray-900">
              KES {medicine.price.toLocaleString()}
            </p>
            <p className={`text-xs font-medium ${
              medicine.stock === 0 ? 'text-red-600' : 
              medicine.stock < 10 ? 'text-orange-600' : 
              'text-green-600'
            }`}>
              {medicine.stock === 0 ? 'Out of Stock' : 
               medicine.stock < 10 ? `Low Stock (${medicine.stock})` : 
               `In Stock (${medicine.stock})`}
            </p>
          </div>

          <Button
            size="sm"
            onClick={() => handleOrderClick(medicine)}
            disabled={medicine.stock === 0 || !medicine.isAvailable}
          >
            <ShoppingCart className="w-4 h-4 mr-1" />
            Order
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Order Medicine</h1>
        <p className="mt-1 text-sm text-gray-500">
          Browse and order medicines from nearby pharmacies
        </p>
      </div>

      {/* Success Alert */}
      {success && (
        <Alert type="success" title="Success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert type="error" title="Error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Pharmacy Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="font-medium text-gray-900 mb-4">Find Pharmacies</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Select
            value={filters.maxDistance}
            onChange={(e) => setFilters(prev => ({ ...prev, maxDistance: parseFloat(e.target.value) }))}
          >
            <option value="5">Within 5 km</option>
            <option value="10">Within 10 km</option>
            <option value="20">Within 20 km</option>
            <option value="50">Within 50 km</option>
          </Select>

          <Select
            value={filters.sortBy}
            onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
          >
            <option value="distance">Nearest First</option>
            <option value="rating">Highest Rated</option>
            <option value="name">Name A-Z</option>
          </Select>

          <label className="flex items-center space-x-2 border rounded-lg px-4">
            <input
              type="checkbox"
              checked={filters.deliveryService}
              onChange={(e) => setFilters(prev => ({ ...prev, deliveryService: e.target.checked }))}
              className="rounded text-primary-600"
            />
            <span className="text-sm text-gray-700">Delivery Only</span>
          </label>

          <Button 
            variant="outline" 
            onClick={loadNearbyPharmacies}
            disabled={loading || !userLocation}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </div>

        {userLocation && (
          <p className="text-xs text-gray-500 mt-3">
            📍 Showing pharmacies within {filters.maxDistance}km of your location
          </p>
        )}
      </div>

      {/* Pharmacies Grid */}
      <div>
        <h3 className="font-medium text-gray-900 mb-4">
          Nearby Pharmacies ({pharmacies.length})
        </h3>
        {locationLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" text="Getting your location..." />
          </div>
        ) : loading && !selectedPharmacy ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" text="Finding pharmacies..." />
          </div>
        ) : pharmacies.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pharmacies.map(pharmacy => (
              <PharmacyCard key={pharmacy._id} pharmacy={pharmacy} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-lg border">
            <Store className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">No pharmacies found nearby</p>
            {!userLocation && (
              <Button onClick={getCurrentLocation} variant="outline" size="sm">
                Enable Location
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Medicine Browse */}
      {selectedPharmacy && (
        <div>
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-primary-900">
              <Store className="w-4 h-4 inline mr-1" />
              Browsing medicines from: <strong>{selectedPharmacy.name}</strong>
            </p>
          </div>

          {/* Medicine Filters */}
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Input
                  icon={Search}
                  placeholder="Search medicines..."
                  value={medicineSearch}
                  onChange={(e) => setMedicineSearch(e.target.value)}
                />
              </div>
              <Select
                value={medicineCategory}
                onChange={(e) => setMedicineCategory(e.target.value)}
              >
                <option value="">All Categories</option>
                {medicineCategories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {/* Medicines Grid */}
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" text="Loading medicines..." />
            </div>
          ) : medicines.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {medicines.map(medicine => (
                <MedicineCard key={medicine._id} medicine={medicine} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg border">
              <Pill className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No medicines found</p>
              {(medicineSearch || medicineCategory) && (
                <Button 
                  onClick={() => {
                    setMedicineSearch('');
                    setMedicineCategory('');
                  }} 
                  variant="outline" 
                  size="sm"
                  className="mt-3"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Order Modal */}
      {showOrderModal && selectedMedicine && (
        <OrderModal
          medicine={selectedMedicine}
          pharmacy={selectedPharmacy}
          user={user}
          onClose={() => {
            setShowOrderModal(false);
            setSelectedMedicine(null);
          }}
          onSuccess={handleOrderSuccess}
        />
      )}
    </div>
  );
};

// Order Modal Component
const OrderModal = ({ medicine, pharmacy, user, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('delivery');
  const [prescription, setPrescription] = useState(null);
  const [prescriptionPreview, setPrescriptionPreview] = useState(null);
  const [notes, setNotes] = useState('');

  const subtotal = medicine.price * quantity;
  const deliveryFee = deliveryMethod === 'delivery' ? (pharmacy.deliveryFee || 200) : 0;
  const total = subtotal + deliveryFee;

  const handlePrescriptionChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setError('Only JPEG, PNG, and PDF files are allowed');
        return;
      }

      setPrescription(file);
      
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => setPrescriptionPreview(reader.result);
        reader.readAsDataURL(file);
      } else {
        setPrescriptionPreview(null);
      }
      
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (medicine.prescriptionRequired && !prescription) {
      setError('Prescription is required for this medicine');
      return;
    }

    if (deliveryMethod === 'delivery' && !deliveryAddress.trim()) {
      setError('Delivery address is required');
      return;
    }

    if (quantity > medicine.stock) {
      setError(`Only ${medicine.stock} units available`);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const formData = new FormData();
      formData.append('medicineId', medicine._id);
      formData.append('pharmacyId', pharmacy._id);
      formData.append('quantity', quantity);
      formData.append('deliveryMethod', deliveryMethod);
      
      if (deliveryMethod === 'delivery') {
        formData.append('deliveryAddress', deliveryAddress);
      }
      
      if (notes) {
        formData.append('notes', notes);
      }
      
      if (prescription) {
        formData.append('file', prescription);
      }

      const token = localStorage.getItem('medigo_token');
      
      if (!token) {
        setError('Authentication required. Please login again.');
        setTimeout(() => {
          window.location.href = '/auth/login';
        }, 2000);
        return;
      }

      const response = await fetch('https://medigo-api.onrender.com/api/orders/create', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      let data;
      try {
        const text = await response.text();
        if (text) {
          data = JSON.parse(text);
        } else {
          throw new Error('Empty response from server');
        }
      } catch (parseError) {
        throw new Error('Invalid server response. Please try again.');
      }

      if (response.ok && data.success) {
        onSuccess();
      } else {
        setError(data.message || data.error || 'Failed to place order. Please try again.');
      }
    } catch (err) {
      console.error('Order error:', err);
      
      if (err.message.includes('Failed to fetch')) {
        setError('Network error. Please check your connection and try again.');
      } else if (err.message.includes('NetworkError')) {
        setError('Unable to reach the server. Please try again later.');
      } else {
        setError(err.message || 'Failed to place order. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Place Order" size="xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <button
              type="button"
              onClick={() => setError('')}
              className="text-red-400 hover:text-red-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Medicine Info */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900">{medicine.name}</h3>
          {medicine.genericName && (
            <p className="text-sm text-gray-600">{medicine.genericName}</p>
          )}
          <p className="text-lg font-bold text-green-600 mt-1">
            KES {medicine.price.toLocaleString()}
          </p>
        </div>

        {/* Prescription Warning */}
        {medicine.prescriptionRequired && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-orange-900">Prescription Required</p>
              <p className="text-xs text-orange-700 mt-1">
                This medicine requires a valid prescription from a licensed doctor
              </p>
            </div>
          </div>
        )}

        {/* Quantity */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quantity
          </label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              className="w-10 h-10 rounded-lg border border-gray-300 hover:bg-gray-50 flex items-center justify-center"
            >
              <Minus className="w-4 h-4" />
            </button>
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, Math.min(medicine.stock, parseInt(e.target.value) || 1)))}
              className="w-20 text-center px-3 py-2 border border-gray-300 rounded-lg"
              min="1"
              max={medicine.stock}
            />
            <button
              type="button"
              onClick={() => setQuantity(Math.min(medicine.stock, quantity + 1))}
              className="w-10 h-10 rounded-lg border border-gray-300 hover:bg-gray-50 flex items-center justify-center"
              disabled={quantity >= medicine.stock}
            >
              <Plus className="w-4 h-4" />
            </button>
            <span className="text-sm text-gray-600">
              (Max: {medicine.stock} available)
            </span>
          </div>
        </div>

        {/* Delivery Method */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Delivery Method
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setDeliveryMethod('delivery')}
              className={`p-4 border-2 rounded-lg text-left transition-colors ${
                deliveryMethod === 'delivery'
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <p className="font-medium">Home Delivery</p>
              <p className="text-sm text-gray-600">KES {pharmacy.deliveryFee || 200}</p>
            </button>
            <button
              type="button"
              onClick={() => setDeliveryMethod('pickup')}
              className={`p-4 border-2 rounded-lg text-left transition-colors ${
                deliveryMethod === 'pickup'
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <p className="font-medium">Pickup</p>
              <p className="text-sm text-gray-600">Free</p>
            </button>
          </div>
        </div>

        {/* Delivery Address */}
        {deliveryMethod === 'delivery' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Delivery Address *
            </label>
            <textarea
              value={deliveryAddress}
              onChange={(e) => setDeliveryAddress(e.target.value)}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="Enter your complete delivery address..."
              required={deliveryMethod === 'delivery'}
            />
          </div>
        )}

        {/* Prescription Upload */}
        {medicine.prescriptionRequired && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Prescription *
            </label>
            
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">
                  {prescription ? prescription.name : 'Click to upload prescription'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  JPEG, PNG, or PDF (Max 5MB)
                </p>
              </div>
              <input
                type="file"
                className="hidden"
                accept="image/jpeg,image/jpg,image/png,application/pdf"
                onChange={handlePrescriptionChange}
                required={medicine.prescriptionRequired}
              />
            </label>

            {prescriptionPreview && (
              <img
                src={prescriptionPreview}
                alt="Prescription"
                className="mt-3 max-h-48 rounded-lg border border-gray-200"
              />
            )}

            {prescription && prescription.type === 'application/pdf' && (
              <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                <FileText className="w-4 h-4" />
                <span>{prescription.name}</span>
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Special Instructions (Optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows="2"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="Any special instructions..."
          />
        </div>

        {/* Order Summary */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
          <h3 className="font-semibold text-gray-900 mb-3">Order Summary</h3>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Subtotal ({quantity} × KES {medicine.price})</span>
            <span className="font-medium">KES {subtotal.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Delivery Fee</span>
            <span className="font-medium">KES {deliveryFee.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
            <span>Total</span>
            <span className="text-green-600">KES {total.toLocaleString()}</span>
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Processing Order...</span>
            </>
          ) : (
            <>
              <ShoppingCart className="w-5 h-5" />
              <span>Place Order - KES {total.toLocaleString()}</span>
            </>
          )}
        </button>

        <p className="text-xs text-gray-500 text-center">
          By placing this order, you agree to our terms and conditions
        </p>
      </form>
    </Modal>
  );
};

export default OrderMedicine;